"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { MouseEvent, ChangeEvent } from "react";
import { Icon } from "@astryxdesign/core/Icon";
import { Text } from "@astryxdesign/core/Text";
import { VStack } from "@astryxdesign/core/VStack";
import { researchIcons } from "@/lib/icons/research-icons";

type DomText = globalThis.Text;
import {
  compactStyledText,
  LANDING_FONT_FAMILIES,
  LANDING_FONT_SIZE_PX,
  LANDING_TEXT_COLOR_PRESETS,
  LANDING_TEXT_COLOR_PRESETS_DARK,
  LANDING_COLOR_DARK_VAR,
  LANDING_COLOR_LIGHT_VAR,
  LANDING_SIZE_LIGHT_VAR,
  LANDING_SIZE_DARK_VAR,
  LANDING_WEIGHT_LIGHT_VAR,
  LANDING_WEIGHT_DARK_VAR,
  LANDING_STYLE_LIGHT_VAR,
  LANDING_STYLE_DARK_VAR,
  LANDING_DECO_LIGHT_VAR,
  LANDING_DECO_DARK_VAR,
  LANDING_FONT_LIGHT_VAR,
  LANDING_FONT_DARK_VAR,
  LANDING_RICH_COLOR_CLASS,
  LANDING_RICH_THEME_CLASS,
  LANDING_THEME_STYLE_VARS,
  resolveStyledText,
  type LandingTextAlign,
  type LandingTextValue,
} from "@/lib/landing/puck/styled-text";
import {
  escapeTextToHtml,
  landingHtmlToPlain,
  normalizeEditorHtml,
} from "@/lib/landing/puck/landing-rich-html";

type LandingStyledTextFieldProps = {
  value: LandingTextValue;
  onChange: (value: LandingTextValue) => void;
  label?: string;
  multiline?: boolean;
  /** Канвас: короткая подсказка, стили по выделенному фрагменту. */
  compact?: boolean;
  /** Тема превью лендинга — подсветить соответствующую палитру. */
  previewDark?: boolean;
  registerFlush?: (flush: () => void) => void;
  /**
   * Только инструменты (тема / шрифт / цвет). Текст правится во внешнем
   * contentEditable на канвасе (`hostEditor`).
   */
  toolbarOnly?: boolean;
  hostEditor?: HTMLElement | null;
  /** Канвас: синхронизация превью светлой/тёмной темы на hostEditor. */
  onEditThemeChange?: (theme: "light" | "dark") => void;
};

const compactControl =
  "border-border bg-[var(--color-background-surface)] text-[var(--color-text-primary)]";
const sidebarControl = "border-border bg-canvas text-primary";

function StyleToggle({
  label,
  icon,
  compact,
  active,
  onMouseDown,
}: {
  label: string;
  icon: (typeof researchIcons)[keyof typeof researchIcons];
  compact?: boolean;
  active?: boolean;
  onMouseDown: (event: MouseEvent<HTMLButtonElement>) => void;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      onMouseDown={onMouseDown}
      className={`inline-flex h-7 w-7 items-center justify-center rounded-md border hover:bg-[var(--color-background-muted)] ${
        compact ? compactControl : sidebarControl
      } ${active ? "bg-[var(--color-background-muted)]" : ""}`}
    >
      <Icon icon={icon} size="sm" />
    </button>
  );
}

function ColorRow({
  label,
  presets,
  compact,
  emphasized,
  onPick,
  onSaveSelection,
}: {
  label: string;
  presets: { label: string; value: string }[];
  compact?: boolean;
  emphasized?: boolean;
  onPick: (color: string) => void;
  onSaveSelection: () => void;
}) {
  return (
    <div
      className={`flex w-full min-w-0 flex-wrap items-center gap-1.5 rounded-lg p-1.5 ${
        emphasized ? "ring-1 ring-border bg-[var(--color-background-muted)]" : ""
      }`}
    >
      <span
        className={`w-full text-xs ${
          compact ? "text-[var(--color-text-secondary)]" : "text-secondary"
        }`}
      >
        {label}
      </span>
      {presets.map((swatch) => (
        <button
          key={`${label}-${swatch.value}`}
          type="button"
          title={swatch.label}
          aria-label={`${label}: ${swatch.label}`}
          className="h-7 w-7 rounded-md border border-border"
          style={{ backgroundColor: swatch.value }}
          onMouseDown={(event) => {
            event.preventDefault();
            onSaveSelection();
            onPick(swatch.value);
          }}
        />
      ))}
      <input
        type="color"
        aria-label={`${label}, свой`}
        title={`${label}, свой`}
        className={`h-7 w-7 cursor-pointer rounded-md border p-0 ${
          compact ? compactControl : sidebarControl
        }`}
        onMouseDown={onSaveSelection}
        onChange={(event) => onPick(event.target.value)}
      />
    </div>
  );
}

function toEditorHtml(value: LandingTextValue): string {
  const parsed = resolveStyledText(value);
  if (parsed.html) return parsed.html;
  return escapeTextToHtml(parsed.text);
}

function collectThemeVarsFromEl(el: HTMLElement): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key of LANDING_THEME_STYLE_VARS) {
    const value = el.style.getPropertyValue(key).trim();
    if (value) out[key] = value;
  }
  return out;
}

function applyVarsToEl(el: HTMLElement, style: Record<string, string>) {
  for (const [key, cssValue] of Object.entries(style)) {
    if (key.startsWith("--")) el.style.setProperty(key, cssValue);
  }
  const merged = { ...collectThemeVarsFromEl(el), ...style };
  const hasColor = Boolean(
    merged[LANDING_COLOR_LIGHT_VAR] || merged[LANDING_COLOR_DARK_VAR],
  );
  const hasTypo = LANDING_THEME_STYLE_VARS.some(
    (key) =>
      key !== LANDING_COLOR_LIGHT_VAR &&
      key !== LANDING_COLOR_DARK_VAR &&
      merged[key],
  );
  if (hasColor) el.classList.add(LANDING_RICH_COLOR_CLASS);
  if (hasTypo) el.classList.add(LANDING_RICH_THEME_CLASS);
}

function makeThemeSpan(doc: Document, style: Record<string, string>): HTMLSpanElement {
  const span = doc.createElement("span");
  applyVarsToEl(span, style);
  return span;
}

const RICH_SELECTOR = `.${LANDING_RICH_COLOR_CLASS}, .${LANDING_RICH_THEME_CLASS}`;

function splitOuterAroundInners(outer: HTMLElement) {
  const parent = outer.parentNode;
  if (!parent) return;
  const doc = outer.ownerDocument;
  const outerVars = collectThemeVarsFromEl(outer);
  const frag = doc.createDocumentFragment();
  for (const child of [...outer.childNodes]) {
    if (child instanceof HTMLElement && child.matches(RICH_SELECTOR)) {
      applyVarsToEl(child, outerVars);
      frag.appendChild(child);
    } else {
      const wrap = makeThemeSpan(doc, outerVars);
      wrap.appendChild(child);
      frag.appendChild(wrap);
    }
  }
  parent.replaceChild(frag, outer);
}

/** Вложенные span цветов → соседи: у каждого фрагмента light и dark на одном теге. */
function explodeNestedRich(root: ParentNode) {
  let nested = root.querySelector(`${RICH_SELECTOR} ${RICH_SELECTOR}`);
  let guard = 0;
  while (nested instanceof HTMLElement && guard < 40) {
    guard += 1;
    const outer = nested.parentElement?.closest(RICH_SELECTOR);
    if (!(outer instanceof HTMLElement) || !root.contains(outer)) break;
    splitOuterAroundInners(outer);
    nested = root.querySelector(`${RICH_SELECTOR} ${RICH_SELECTOR}`);
  }
}

function richSpanWrapsOnlyTextNode(rich: HTMLElement, textNode: DomText): boolean {
  return rich === textNode.parentElement && rich.childNodes.length === 1;
}

function themeVarsEqual(
  a: Record<string, string>,
  b: Record<string, string>,
): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    if ((a[key] ?? "") !== (b[key] ?? "")) return false;
  }
  return true;
}

/** Соседние span с одинаковыми vars → один span (меньше «лоскутов»). */
function mergeAdjacentRichSpans(root: ParentNode) {
  const spans = [...root.querySelectorAll(RICH_SELECTOR)].filter(
    (el): el is HTMLElement => el instanceof HTMLElement,
  );
  for (const span of spans) {
    if (!span.isConnected) continue;
    let next = span.nextSibling;
    while (next instanceof HTMLElement && next.matches(RICH_SELECTOR)) {
      if (!themeVarsEqual(collectThemeVarsFromEl(span), collectThemeVarsFromEl(next))) {
        break;
      }
      while (next.firstChild) span.appendChild(next.firstChild);
      const removed = next;
      next = removed.nextSibling;
      removed.remove();
    }
  }
}

function normalizeRichTree(root: ParentNode) {
  mergeAdjacentRichSpans(root);
  explodeNestedRich(root);
  mergeAdjacentRichSpans(root);
}

function splitTextNode(node: Node, offset: number): DomText {
  if (node.nodeType !== Node.TEXT_NODE) {
    throw new Error("splitTextNode: not a text node");
  }
  return (node as DomText).splitText(offset);
}

function splitRangeTextBoundaries(range: Range): Range {
  const work = range.cloneRange();

  if (work.endContainer.nodeType === Node.TEXT_NODE && work.endOffset > 0) {
    const endText = work.endContainer;
    const len = endText.textContent?.length ?? 0;
    if (work.endOffset < len) {
      splitTextNode(endText, work.endOffset);
    }
  }

  if (work.startContainer.nodeType === Node.TEXT_NODE && work.startOffset > 0) {
    const startText = work.startContainer;
    const len = startText.textContent?.length ?? 0;
    if (work.startOffset < len) {
      const after = splitTextNode(startText, work.startOffset);
      work.setStart(after, 0);
    }
  }

  return work;
}

function textNodesFullyInRange(editor: HTMLElement, range: Range): DomText[] {
  const doc = editor.ownerDocument;
  const normalized = splitRangeTextBoundaries(range);
  const nodes: DomText[] = [];
  const walker = doc.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
  let node: Node | null = walker.nextNode();
  while (node) {
    if (!normalized.intersectsNode(node)) {
      node = walker.nextNode();
      continue;
    }
    const text = node as DomText;
    if (!text.textContent) {
      node = walker.nextNode();
      continue;
    }
    const nodeRange = doc.createRange();
    nodeRange.selectNodeContents(text);
    if (
      normalized.compareBoundaryPoints(Range.START_TO_START, nodeRange) <= 0 &&
      normalized.compareBoundaryPoints(Range.END_TO_END, nodeRange) >= 0
    ) {
      nodes.push(text);
    }
    node = walker.nextNode();
  }
  return nodes;
}

/**
 * Light/dark — два слоя vars на одном тексте: правим только переданные vars,
 * существующие vars другой темы сохраняем.
 */
function applyStyleToRange(
  editor: HTMLElement,
  range: Range,
  style: Record<string, string>,
) {
  const doc = editor.ownerDocument;
  for (const textNode of textNodesFullyInRange(editor, range)) {
    const rich = textNode.parentElement?.closest(RICH_SELECTOR);
    if (
      rich instanceof HTMLElement &&
      editor.contains(rich) &&
      richSpanWrapsOnlyTextNode(rich, textNode)
    ) {
      applyVarsToEl(rich, style);
      continue;
    }

    const inherited =
      rich instanceof HTMLElement && editor.contains(rich)
        ? collectThemeVarsFromEl(rich)
        : {};
    const wrap = makeThemeSpan(doc, { ...inherited, ...style });
    const parent = textNode.parentNode;
    if (!parent) continue;
    parent.replaceChild(wrap, textNode);
    wrap.appendChild(textNode);
  }
  normalizeRichTree(editor);
}

function editorWindow(editor: HTMLElement) {
  return editor.ownerDocument.defaultView ?? window;
}

type SelectionBookmark = { start: number; end: number };

/** Смещения символов — переживают explodeNestedRich, в отличие от Range. */
function rangeToBookmark(
  editor: HTMLElement,
  range: Range,
): SelectionBookmark | null {
  if (range.collapsed || !editor.contains(range.commonAncestorContainer)) {
    return null;
  }
  const doc = editor.ownerDocument;
  const probe = doc.createRange();
  probe.selectNodeContents(editor);
  probe.setEnd(range.startContainer, range.startOffset);
  const start = probe.toString().length;
  probe.setEnd(range.endContainer, range.endOffset);
  const end = probe.toString().length;
  if (start >= end) return null;
  return { start, end };
}

function bookmarkToRange(
  editor: HTMLElement,
  bookmark: SelectionBookmark,
): Range | null {
  const doc = editor.ownerDocument;
  const walker = doc.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
  let pos = 0;
  let startNode: Node | null = null;
  let startOffset = 0;
  let endNode: Node | null = null;
  let endOffset = 0;
  let node: Node | null = walker.nextNode();
  while (node) {
    const len = node.textContent?.length ?? 0;
    if (!startNode && pos + len >= bookmark.start) {
      startNode = node;
      startOffset = bookmark.start - pos;
    }
    if (!endNode && pos + len >= bookmark.end) {
      endNode = node;
      endOffset = bookmark.end - pos;
      break;
    }
    pos += len;
    node = walker.nextNode();
  }
  if (!startNode || !endNode) return null;
  const range = doc.createRange();
  try {
    range.setStart(startNode, startOffset);
    range.setEnd(endNode, endOffset);
    return range;
  } catch {
    return null;
  }
}


export function LandingStyledTextField({
  value,
  onChange,
  label = "Текст",
  multiline = false,
  compact = false,
  previewDark,
  registerFlush,
  toolbarOnly = false,
  hostEditor = null,
  onEditThemeChange,
}: LandingStyledTextFieldProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const lastHtmlRef = useRef(toEditorHtml(value));
  const savedBookmarkRef = useRef<SelectionBookmark | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const [align, setAlign] = useState<LandingTextAlign | undefined>(
    () => resolveStyledText(value).align,
  );
  const alignRef = useRef(align);
  alignRef.current = align;
  const [editTheme, setEditTheme] = useState<"light" | "dark">(() =>
    previewDark ? "dark" : "light",
  );
  const editThemeRef = useRef(editTheme);
  editThemeRef.current = editTheme;
  const selectClass = `h-7 rounded-md border px-1.5 text-xs ${
    compact || toolbarOnly ? compactControl : sidebarControl
  }`;
  const chromeCompact = compact || toolbarOnly;

  useLayoutEffect(() => {
    if (!hostEditor) return;
    editorRef.current = hostEditor as HTMLDivElement;
    lastHtmlRef.current = normalizeEditorHtml(hostEditor.innerHTML);
  }, [hostEditor]);

  useEffect(() => {
    onEditThemeChange?.(editTheme);
  }, [editTheme, onEditThemeChange]);

  useEffect(() => {
    if (!toolbarOnly || !hostEditor) return;
    const onSelect = () => saveSelection();
    hostEditor.addEventListener("keyup", onSelect);
    hostEditor.addEventListener("mouseup", onSelect);
    hostEditor.addEventListener("focus", onSelect);
    const doc = hostEditor.ownerDocument;
    const onSelChange = () => {
      if (doc.activeElement !== hostEditor) return;
      const selection = editorWindow(hostEditor).getSelection();
      // Не затирать сохранённое выделение пустым после смены темы/ре-рендера.
      if (!selection || selection.rangeCount === 0) return;
      if (selection.getRangeAt(0).collapsed) return;
      saveSelection();
    };
    doc.addEventListener("selectionchange", onSelChange);
    return () => {
      hostEditor.removeEventListener("keyup", onSelect);
      hostEditor.removeEventListener("mouseup", onSelect);
      hostEditor.removeEventListener("focus", onSelect);
      doc.removeEventListener("selectionchange", onSelChange);
    };
  }, [toolbarOnly, hostEditor]);

  useEffect(() => {
    if (toolbarOnly) return;
    const editor = editorRef.current;
    if (!editor) return;
    const next = toEditorHtml(value);
    if (next === lastHtmlRef.current) return;
    const doc = editor.ownerDocument;
    if (doc.activeElement === editor) return;
    editor.innerHTML = next || "";
    lastHtmlRef.current = next;
  }, [value, toolbarOnly]);

  function saveSelection() {
    const editor = editorRef.current;
    if (!editor) return;
    const selection = editorWindow(editor).getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (range.collapsed) return;
    const bookmark = rangeToBookmark(editor, range);
    if (bookmark) savedBookmarkRef.current = bookmark;
  }

  function restoreSelection() {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    const selection = editorWindow(editor).getSelection();
    if (!selection) return;

    if (selection.rangeCount > 0) {
      const live = selection.getRangeAt(0);
      if (!live.collapsed && editor.contains(live.commonAncestorContainer)) {
        const bookmark = rangeToBookmark(editor, live);
        if (bookmark) {
          savedBookmarkRef.current = bookmark;
          return;
        }
      }
    }

    selection.removeAllRanges();
    const bookmark = savedBookmarkRef.current;
    if (bookmark) {
      const range = bookmarkToRange(editor, bookmark);
      if (range) {
        selection.addRange(range);
        return;
      }
      savedBookmarkRef.current = null;
    }
    const fallback = editor.ownerDocument.createRange();
    fallback.selectNodeContents(editor);
    selection.addRange(fallback);
  }

  function restoreBookmark(bookmark: SelectionBookmark | null) {
    if (!bookmark) return;
    savedBookmarkRef.current = bookmark;
    restoreSelection();
  }

  function wrapSelection(style: Record<string, string>) {
    restoreSelection();
    const editor = editorRef.current;
    if (!editor) return;
    const doc = editor.ownerDocument;
    const selection = editorWindow(editor).getSelection();
    if (!selection) return;
    if (selection.rangeCount === 0 || selection.getRangeAt(0).collapsed) {
      const caret = selection.rangeCount ? selection.getRangeAt(0) : null;
      const caretEl =
        caret?.startContainer instanceof Element
          ? caret.startContainer
          : caret?.startContainer.parentElement;
      const caretRich = caretEl?.closest(RICH_SELECTOR);
      if (caretRich instanceof HTMLElement && editor.contains(caretRich)) {
        const caretBookmark = caret ? rangeToBookmark(editor, caret) : null;
        applyVarsToEl(caretRich, style);
        normalizeRichTree(editor);
        restoreBookmark(caretBookmark);
        return;
      }
      if (caret && caret.startContainer.nodeType === Node.TEXT_NODE) {
        const textNode = caret.startContainer;
        const value = textNode.textContent ?? "";
        let start = caret.startOffset;
        let end = caret.startOffset;
        while (start > 0 && !/\s/.test(value[start - 1] ?? "")) start -= 1;
        while (end < value.length && !/\s/.test(value[end] ?? "")) end += 1;
        if (end > start) {
          const word = doc.createRange();
          word.setStart(textNode, start);
          word.setEnd(textNode, end);
          selection.removeAllRanges();
          selection.addRange(word);
        } else {
          return;
        }
      } else {
        return;
      }
    }
    if (selection.rangeCount === 0 || selection.getRangeAt(0).collapsed) return;
    const liveRange = selection.getRangeAt(0);
    const editBookmark = rangeToBookmark(editor, liveRange);
    applyStyleToRange(editor, liveRange, style);
    restoreBookmark(editBookmark);
  }

  function emitFromEditor() {
    const editor = editorRef.current;
    if (!editor) return;
    const bookmark = savedBookmarkRef.current;
    normalizeRichTree(editor);
    const html = normalizeEditorHtml(editor.innerHTML);
    lastHtmlRef.current = html;
    onChangeRef.current(
      compactStyledText({
        text: landingHtmlToPlain(html),
        html,
        ...(alignRef.current ? { align: alignRef.current } : {}),
      }),
    );
    restoreBookmark(bookmark);
  }

  useLayoutEffect(() => {
    registerFlush?.(emitFromEditor);
  });

  useEffect(() => {
    return () => {
      emitFromEditor();
    };
  }, []);

  useEffect(() => {
    if (!compact || toolbarOnly) return;
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    const doc = editor.ownerDocument;
    const range = doc.createRange();
    range.selectNodeContents(editor);
    range.collapse(false);
    const selection = editorWindow(editor).getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
    saveSelection();
  }, [compact, toolbarOnly]);

  function applyAlign(event: MouseEvent, next: LandingTextAlign) {
    event.preventDefault();
    setAlign(next);
    alignRef.current = next;
    const editor = editorRef.current;
    if (editor) editor.style.textAlign = next;
    emitFromEditor();
  }

  function wrapAndEmit(style: Record<string, string>) {
    wrapSelection(style);
    emitFromEditor();
  }

  function themeVar(
    kind: "color" | "size" | "weight" | "style" | "deco" | "font",
  ): string {
    const dark = editThemeRef.current === "dark";
    if (kind === "color") return dark ? LANDING_COLOR_DARK_VAR : LANDING_COLOR_LIGHT_VAR;
    if (kind === "size") return dark ? LANDING_SIZE_DARK_VAR : LANDING_SIZE_LIGHT_VAR;
    if (kind === "weight") return dark ? LANDING_WEIGHT_DARK_VAR : LANDING_WEIGHT_LIGHT_VAR;
    if (kind === "style") return dark ? LANDING_STYLE_DARK_VAR : LANDING_STYLE_LIGHT_VAR;
    if (kind === "deco") return dark ? LANDING_DECO_DARK_VAR : LANDING_DECO_LIGHT_VAR;
    return dark ? LANDING_FONT_DARK_VAR : LANDING_FONT_LIGHT_VAR;
  }

  function applyThemeFormat(event: MouseEvent, kind: "weight" | "style" | "deco", cssValue: string) {
    event.preventDefault();
    wrapAndEmit({ [themeVar(kind)]: cssValue });
  }

  function onToolbarSelect(
    event: ChangeEvent<HTMLSelectElement>,
    style: Record<string, string>,
  ) {
    wrapSelection(style);
    emitFromEditor();
    event.currentTarget.selectedIndex = 0;
  }

  const editorBox = (
      <div
        ref={(node) => {
          editorRef.current = node;
          if (node && node.innerHTML === "" && lastHtmlRef.current) {
            node.innerHTML = lastHtmlRef.current;
          }
        }}
        data-landing-rich
        data-landing-resolved={editTheme}
        role="textbox"
        aria-label={label}
        aria-multiline="true"
        contentEditable
        suppressContentEditableWarning
        className={[
          "landing-rich-preview w-full min-w-0 rounded-md border px-2 py-1.5 text-sm",
          "break-words [overflow-wrap:anywhere] outline-none focus:border-accent",
          editTheme === "dark"
            ? "border-[#444] bg-[#1c1c1c] text-[#f3f3f3]"
            : compact
              ? compactControl
              : sidebarControl,
          multiline ? "min-h-24" : compact ? "min-h-11" : "min-h-10",
        ].join(" ")}
        style={align ? { textAlign: align } : undefined}
        onInput={emitFromEditor}
        onBlur={emitFromEditor}
        onKeyUp={(event) => {
          event.stopPropagation();
          saveSelection();
        }}
        onMouseUp={saveSelection}
        onKeyDown={(event) => {
          event.stopPropagation();
          if (event.key !== "Enter") return;
          event.preventDefault();
          restoreSelection();
          const editor = editorRef.current;
          const doc = editor?.ownerDocument ?? document;
          if (!doc.execCommand("insertLineBreak")) {
            const selection = editor
              ? editorWindow(editor).getSelection()
              : window.getSelection();
            if (selection && selection.rangeCount > 0) {
              const range = selection.getRangeAt(0);
              range.deleteContents();
              const br = doc.createElement("br");
              range.insertNode(br);
              range.setStartAfter(br);
              range.collapse(true);
              selection.removeAllRanges();
              selection.addRange(range);
            }
          }
          emitFromEditor();
          saveSelection();
        }}
      />
  );

  const toolbar = (
      <div className="flex flex-wrap items-center gap-1.5">
        <select
          aria-label="Шрифт"
          defaultValue=""
          className={`${selectClass} max-w-[9.5rem]`}
          onMouseDown={saveSelection}
          onChange={(event) => {
            const family = event.target.value;
            onToolbarSelect(
              event,
              family
                ? { [themeVar("font")]: family }
                : { [themeVar("font")]: "Arial, Helvetica, sans-serif" },
            );
          }}
        >
          <option value="" disabled>
            Шрифт
          </option>
          {LANDING_FONT_FAMILIES.map((item) => (
            <option key={item.label} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
        <select
          aria-label="Размер шрифта"
          defaultValue=""
          className={selectClass}
          onMouseDown={saveSelection}
          onChange={(event) => {
            const px = event.target.value;
            if (!px) return;
            onToolbarSelect(event, { [themeVar("size")]: `${px}px` });
          }}
        >
          <option value="" disabled>
            Размер
          </option>
          {LANDING_FONT_SIZE_PX.map((size) => (
            <option key={size} value={size}>
              {size}px
            </option>
          ))}
        </select>
        <StyleToggle
          label="Жирный"
          icon={researchIcons.bold}
          compact={chromeCompact}
          onMouseDown={(event) => applyThemeFormat(event, "weight", "700")}
        />
        <StyleToggle
          label="Курсив"
          icon={researchIcons.italic}
          compact={chromeCompact}
          onMouseDown={(event) => applyThemeFormat(event, "style", "italic")}
        />
        <StyleToggle
          label="Подчёркнутый"
          icon={researchIcons.underline}
          compact={chromeCompact}
          onMouseDown={(event) => applyThemeFormat(event, "deco", "underline")}
        />
        <StyleToggle
          label="Зачёркнутый"
          icon={researchIcons.strikethrough}
          compact={chromeCompact}
          onMouseDown={(event) => applyThemeFormat(event, "deco", "line-through")}
        />
        <StyleToggle
          label="По левому краю"
          icon={researchIcons.alignLeft}
          compact={chromeCompact}
          active={align === "left"}
          onMouseDown={(event) => applyAlign(event, "left")}
        />
        <StyleToggle
          label="По центру"
          icon={researchIcons.alignCenter}
          compact={chromeCompact}
          active={align === "center"}
          onMouseDown={(event) => applyAlign(event, "center")}
        />
        <StyleToggle
          label="По правому краю"
          icon={researchIcons.alignRight}
          compact={chromeCompact}
          active={align === "right"}
          onMouseDown={(event) => applyAlign(event, "right")}
        />
      </div>
  );

  const themeSwitch = (
    <div className="flex w-full min-w-0 flex-col gap-1.5">
      <span
        className={`text-xs ${
          chromeCompact ? "text-[var(--color-text-secondary)]" : "text-secondary"
        }`}
      >
        Какую тему правите
      </span>
      <div className="flex flex-wrap items-center gap-1">
        {(
          [
            { id: "light" as const, label: "Светлая", icon: researchIcons.sun },
            { id: "dark" as const, label: "Тёмная", icon: researchIcons.moon },
          ] as const
        ).map((item) => {
          const selected = editTheme === item.id;
          return (
            <button
              key={item.id}
              type="button"
              aria-pressed={selected}
              className={`inline-flex h-8 items-center gap-1 rounded-full border px-2.5 text-xs ${
                chromeCompact ? compactControl : sidebarControl
              } ${selected ? "bg-[var(--color-background-muted)] font-medium" : ""}`}
              onMouseDown={(event) => {
                event.preventDefault();
                saveSelection();
              }}
              onClick={() => {
                setEditTheme(item.id);
                onEditThemeChange?.(item.id);
              }}
            >
              <Icon icon={item.icon} size="sm" />
              {item.label}
            </button>
          );
        })}
      </div>
      <p
        className={`text-xs ${
          chromeCompact ? "text-[var(--color-text-secondary)]" : "text-secondary"
        }`}
      >
        {toolbarOnly
          ? editTheme === "dark"
            ? "На канвасе — превью тёмной темы. Цвет и шрифт сохранятся для тёмного режима."
            : "На канвасе — превью светлой темы. Цвет и шрифт сохранятся для светлого режима."
          : editTheme === "dark"
            ? "Поле ниже — как в тёмной теме. Цвет и шрифт сохранятся для тёмного режима сайта."
            : "Поле ниже — как в светлой теме. Цвет и шрифт сохранятся для светлого режима сайта."}
      </p>
    </div>
  );

  const colors = (
    <ColorRow
      label={editTheme === "dark" ? "Цвет в тёмной теме" : "Цвет в светлой теме"}
      compact={chromeCompact}
      presets={
        editTheme === "dark"
          ? LANDING_TEXT_COLOR_PRESETS_DARK
          : LANDING_TEXT_COLOR_PRESETS
      }
      onPick={(color) =>
        wrapAndEmit({
          [editTheme === "dark" ? LANDING_COLOR_DARK_VAR : LANDING_COLOR_LIGHT_VAR]:
            color,
        })
      }
      onSaveSelection={saveSelection}
    />
  );

  return (
    <VStack gap={2} align="start" width="100%">
      {toolbarOnly ? (
        <p className="text-xs text-[var(--color-text-secondary)]">
          Текст правится на канвасе. Здесь — цвет, шрифт и выравнивание.
        </p>
      ) : compact ? (
        <p className="text-xs text-[var(--color-text-secondary)]">
          Выделите слова — к ним применятся цвет и шрифт выбранной темы.
          Выравнивание — ко всему тексту.
        </p>
      ) : (
        <>
          <Text size="sm">{label}</Text>
          <Text size="sm" color="secondary">
            Выделите слова. Переключатель темы меняет, для какого режима
            сохраняются цвет и шрифт. Enter — новая строка.
          </Text>
        </>
      )}
      {!toolbarOnly && compact ? editorBox : null}
      {themeSwitch}
      {toolbar}
      {colors}
      {!toolbarOnly && !compact ? editorBox : null}
    </VStack>
  );
}
